/**
 * Operations Cache
 *
 * IndexedDB-backed cache for the prefetched operations corpus used by the
 * operation picker's client-side search. Keyed by `ops-{region}` — the
 * managed-API catalog is regional, not project-scoped, so two projects in
 * the same region share one cache slot. An LRU policy keeps at most
 * {@link OPERATIONS_CACHE_MAX_ENTRIES} entries to bound disk usage (~4.5MB
 * per cache entry × 3 ≈ 14MB ceiling).
 *
 * This module is best-effort — every operation swallows errors and resolves to
 * a safe default so a failing IndexedDB never blocks the network path.
 */

import type { DiscoveryOperation } from './operationsService';

const DB_NAME = 'otto-operations-cache';
// Version history:
//   v1 → v2: cache key dropped projectId (`ops-{project}-{region}` →
//            `ops-{region}`); the v2 upgrade wipes the legacy store.
//   v2 → v3: introduce `highWaterMarkCount` on the entry. Existing v2
//            entries are wiped because some users ended up with a partial
//            (truncated) corpus that we want to refetch from scratch —
//            without a starting HWM we have no way to distinguish a
//            legitimate small corpus from a stuck partial one.
const DB_VERSION = 3;
const STORE_NAME = 'operations';

/** Max age of a cached entry before it must be refetched. */
export const OPERATIONS_CACHE_TTL_MS = 24 * 60 * 60 * 1000; // 24 hours

/** LRU cap — keep at most this many region entries. */
export const OPERATIONS_CACHE_MAX_ENTRIES = 3;

/**
 * Fraction of the per-region high water mark that a new write must meet to
 * be persisted. Writes below this threshold are refused so a transient
 * partial fetch can't shrink a known-good corpus and lock the picker into
 * a degraded state for the full 24h TTL. 0.8 catches the partial-fetch
 * regressions we've observed while leaving room for legitimate catalog
 * churn between releases.
 */
export const OPERATIONS_CACHE_HWM_MIN_RATIO = 0.8;

export interface OperationsCacheEntry {
  /** Cache key — see {@link buildCacheKey}. */
  key: string;
  /** Region (lowercased). */
  region: string;
  /** Merged operations corpus (built-in + azure + custom). */
  operations: DiscoveryOperation[];
  /** Epoch ms when the entry was written. */
  cachedAt: number;
  /** Epoch ms of the last read — drives LRU eviction. */
  lastAccessedAt: number;
  /**
   * Largest corpus size ever persisted for this region. Set on first write,
   * monotonically non-decreasing thereafter. Used as the floor for accepting
   * subsequent writes (see {@link OPERATIONS_CACHE_HWM_MIN_RATIO}) so a
   * transient partial fetch can't shrink a known-good cache.
   *
   * Optional for backward-compat with v3-initial entries that may exist
   * before the field was written; readers treat `undefined` as "unknown".
   */
  highWaterMarkCount?: number;
}

export const buildCacheKey = (region: string): string =>
  `ops-${region.toLowerCase()}`;

/**
 * Strip an operation down to only the fields the picker, search index, and
 * `discoveryOperationToDefinition` actually read. Reduces a typical Azure
 * managed-API operation from ~1.2KB → ~450B before it lands in IndexedDB,
 * which keeps a three-region LRU under ~40MB even for 30k+ operation corpuses.
 *
 * Fields kept (in sync with everything reached from {@link DiscoveryOperation}
 * consumers across the codebase):
 *   - top-level: `id`, `name`, `type`, `kind`
 *   - properties: `summary`, `description`, `visibility`, `operationType`,
 *                 `brandColor`, `iconUri`, `trigger`, `capabilities`,
 *                 `isWebhook`, `isNotification`, `isCustomApi`
 *   - properties.api: `id`, `name`, `displayName`, `description`, `iconUri`,
 *                     `brandColor`
 *
 * Fields dropped (verified unused by picker + node creation):
 *   `properties.annotation`, `properties.externalDocs`, `properties.pageable`,
 *   `properties.isChunkingSupported`, `properties.api.category`,
 *   `properties.api.type`, `location`, and any other extra payload metadata
 *   the upstream API may return.
 */
export const trimOperationForCache = (op: DiscoveryOperation): DiscoveryOperation => {
  const p = op.properties ?? ({} as DiscoveryOperation['properties']);
  const api = p.api ?? ({ id: '' } as DiscoveryOperation['properties']['api']);
  const trimmed: DiscoveryOperation = {
    id: op.id,
    name: op.name,
    type: op.type,
    properties: {
      api: {
        id: api.id,
      },
    },
  };
  if (op.kind !== undefined) trimmed.kind = op.kind;

  if (p.summary !== undefined) trimmed.properties.summary = p.summary;
  if (p.description !== undefined) trimmed.properties.description = p.description;
  if (p.visibility !== undefined) trimmed.properties.visibility = p.visibility;
  if (p.operationType !== undefined) trimmed.properties.operationType = p.operationType;
  if (p.brandColor !== undefined) trimmed.properties.brandColor = p.brandColor;
  if (p.iconUri !== undefined) trimmed.properties.iconUri = p.iconUri;
  if (p.trigger !== undefined) trimmed.properties.trigger = p.trigger;
  if (p.capabilities !== undefined) trimmed.properties.capabilities = p.capabilities;
  if (p.isWebhook !== undefined) trimmed.properties.isWebhook = p.isWebhook;
  if (p.isNotification !== undefined) trimmed.properties.isNotification = p.isNotification;
  if (p.isCustomApi !== undefined) trimmed.properties.isCustomApi = p.isCustomApi;

  if (api.name !== undefined) trimmed.properties.api.name = api.name;
  if (api.displayName !== undefined) trimmed.properties.api.displayName = api.displayName;
  if (api.description !== undefined) trimmed.properties.api.description = api.description;
  if (api.iconUri !== undefined) trimmed.properties.api.iconUri = api.iconUri;
  if (api.brandColor !== undefined) trimmed.properties.api.brandColor = api.brandColor;

  return trimmed;
};

let dbPromise: Promise<IDBDatabase | null> | null = null;

const openDb = (): Promise<IDBDatabase | null> => {
  if (dbPromise) return dbPromise;
  if (typeof indexedDB === 'undefined') {
    dbPromise = Promise.resolve(null);
    return dbPromise;
  }

  dbPromise = new Promise((resolve) => {
    let request: IDBOpenDBRequest;
    try {
      request = indexedDB.open(DB_NAME, DB_VERSION);
    } catch (err) {
      console.warn('[operationsCache] indexedDB.open failed:', err);
      resolve(null);
      return;
    }

    request.onupgradeneeded = (event) => {
      const db = request.result;
      const oldVersion = (event as IDBVersionChangeEvent).oldVersion;
      // v1 → v2: cache key changed from `ops-{project}-{region}` to
      // `ops-{region}`. Drop the legacy store outright so users don't keep
      // orphaned entries that would never get LRU-evicted because the new
      // code path can't read them.
      // v2 → v3: HWM tracking introduced. Existing v2 entries may be a
      // stuck-partial corpus — wipe them so the next session refetches and
      // establishes a real HWM. This is a one-time forced refresh.
      if (
        oldVersion > 0 &&
        oldVersion < 3 &&
        db.objectStoreNames.contains(STORE_NAME)
      ) {
        db.deleteObjectStore(STORE_NAME);
      }
      if (!db.objectStoreNames.contains(STORE_NAME)) {
        db.createObjectStore(STORE_NAME, { keyPath: 'key' });
      }
    };
    request.onsuccess = () => resolve(request.result);
    request.onerror = () => {
      console.warn('[operationsCache] indexedDB open error:', request.error);
      resolve(null);
    };
    request.onblocked = () => {
      // Another tab is holding an older DB version open. This is transient
      // — once that tab closes or upgrades, a fresh `indexedDB.open()` will
      // succeed. Reset `dbPromise` before resolving so the next caller
      // retries instead of being permanently stuck with the memoized
      // null result for the rest of the session.
      console.warn('[operationsCache] indexedDB open blocked; will retry on next access');
      dbPromise = null;
      resolve(null);
    };
  });

  return dbPromise;
};

/**
 * Run `fn` against the store and resolve when its transaction completes. The
 * callback may queue any number of requests on `store`; the returned promise
 * resolves with whatever the callback returned (after awaiting it) once the
 * transaction itself commits.
 */
const withStore = <T>(
  mode: IDBTransactionMode,
  fn: (store: IDBObjectStore) => Promise<T> | T,
): Promise<T | null> =>
  openDb().then(
    (db) =>
      new Promise<T | null>((resolve) => {
        if (!db) {
          resolve(null);
          return;
        }
        let tx: IDBTransaction;
        try {
          tx = db.transaction(STORE_NAME, mode);
        } catch (err) {
          console.warn('[operationsCache] transaction failed:', err);
          resolve(null);
          return;
        }

        let value: T | null = null;
        let txComplete = false;
        let cbDone = false;

        const maybeResolve = () => {
          if (txComplete && cbDone) resolve(value);
        };

        try {
          Promise.resolve(fn(tx.objectStore(STORE_NAME))).then(
            (v) => {
              value = v ?? null;
              cbDone = true;
              maybeResolve();
            },
            (err) => {
              console.warn('[operationsCache] store op rejected:', err);
              cbDone = true;
              maybeResolve();
            },
          );
        } catch (err) {
          console.warn('[operationsCache] store op threw:', err);
          cbDone = true;
        }

        tx.oncomplete = () => {
          txComplete = true;
          maybeResolve();
        };
        tx.onerror = () => {
          txComplete = true;
          console.warn('[operationsCache] tx error:', tx.error);
          value = null;
          maybeResolve();
        };
        tx.onabort = () => {
          txComplete = true;
          value = null;
          maybeResolve();
        };
      }),
  );

/** Promise-wrap an IDBRequest so it can be awaited inside a withStore callback. */
const requestToPromise = <T>(req: IDBRequest<T>): Promise<T> =>
  new Promise((resolve, reject) => {
    req.onsuccess = () => resolve(req.result);
    req.onerror = () => reject(req.error);
  });

/**
 * Read the entry for `region` from IndexedDB.
 *
 * Returns `null` when the cache is empty, the entry is missing, or the entry
 * has expired (older than {@link OPERATIONS_CACHE_TTL_MS}). A fresh hit
 * updates `lastAccessedAt` so the LRU policy reflects actual usage.
 */
export const readOperationsCache = async (
  region: string,
): Promise<OperationsCacheEntry | null> => {
  if (!region) return null;
  const key = buildCacheKey(region);
  const entry = await withStore<OperationsCacheEntry | undefined>('readonly', (store) =>
    requestToPromise(store.get(key) as IDBRequest<OperationsCacheEntry | undefined>),
  );
  if (!entry) return null;

  const now = Date.now();
  if (now - entry.cachedAt > OPERATIONS_CACHE_TTL_MS) {
    // Stale — delete so the caller refetches.
    void deleteOperationsCacheEntry(region);
    return null;
  }

  // Bump LRU timestamp (fire-and-forget; never block the read).
  void withStore('readwrite', (store) => {
    store.put({ ...entry, lastAccessedAt: now });
  });

  return entry;
};

/**
 * Persist the full operations corpus for `region` and enforce the LRU cap.
 *
 * Applies high-water-mark protection: if the existing entry's
 * `highWaterMarkCount` indicates a previously-observed corpus that's
 * meaningfully larger than the new one (see
 * {@link OPERATIONS_CACHE_HWM_MIN_RATIO}), the write is **refused** — the
 * existing entry stays in place, and the next session retries. This prevents
 * a transient partial fetch (server flake, mid-rollout, etc.) from
 * overwriting a known-good cache and locking users into a degraded picker
 * for the full 24h TTL.
 *
 * On accept, `highWaterMarkCount` is bumped to `max(previous, new)`.
 *
 * @returns `true` if the write happened, `false` if it was refused.
 */
export const writeOperationsCache = async (
  region: string,
  operations: DiscoveryOperation[],
): Promise<boolean> => {
  if (!region) return false;
  const key = buildCacheKey(region);
  const previous = await withStore<OperationsCacheEntry | undefined>(
    'readonly',
    (store) =>
      requestToPromise(
        store.get(key) as IDBRequest<OperationsCacheEntry | undefined>,
      ),
  );
  const previousCount = previous?.operations.length ?? 0;
  const previousHwm = previous?.highWaterMarkCount ?? previousCount;
  const newCount = operations.length;

  // Refuse to overwrite a known-good corpus with a meaningfully smaller one.
  // Only kicks in when we *have* a prior HWM to compare against — first
  // writes for a region always go through (they establish the HWM).
  if (
    previousHwm > 0 &&
    newCount < previousHwm * OPERATIONS_CACHE_HWM_MIN_RATIO
  ) {
    console.warn(
      `[operationsCache] Refusing to write region '${region}' — new corpus has ${newCount} ops ` +
        `but high water mark is ${previousHwm} (min ratio ${OPERATIONS_CACHE_HWM_MIN_RATIO}). ` +
        'Keeping previous entry; treating new payload as a transient partial fetch. ' +
        'If this is intentional (e.g. connectors were removed), clear the cache via dev tools.',
    );
    return false;
  }

  const now = Date.now();
  const entry: OperationsCacheEntry = {
    key,
    region: region.toLowerCase(),
    operations,
    cachedAt: now,
    lastAccessedAt: now,
    highWaterMarkCount: Math.max(previousHwm, newCount),
  };

  await withStore('readwrite', (store) => {
    store.put(entry);
  });
  await enforceLruCap();
  return true;
};

/** Delete a single cache entry. */
export const deleteOperationsCacheEntry = async (
  region: string,
): Promise<void> => {
  if (!region) return;
  await withStore('readwrite', (store) => {
    store.delete(buildCacheKey(region));
  });
};

/** Read every entry's metadata (with the operations payload). */
const readAllEntries = async (): Promise<OperationsCacheEntry[]> => {
  const entries = await withStore('readonly', (store) =>
    requestToPromise(store.getAll() as IDBRequest<OperationsCacheEntry[]>),
  );
  return entries ?? [];
};

/**
 * Drop the least-recently-accessed entries until the store is within the
 * {@link OPERATIONS_CACHE_MAX_ENTRIES} cap. Exported for tests; production
 * callers go through {@link writeOperationsCache}.
 */
export const enforceLruCap = async (): Promise<void> => {
  const entries = await readAllEntries();
  if (entries.length <= OPERATIONS_CACHE_MAX_ENTRIES) return;

  const sorted = [...entries].sort(
    (a, b) => a.lastAccessedAt - b.lastAccessedAt,
  );
  const overflow = sorted.length - OPERATIONS_CACHE_MAX_ENTRIES;
  const toEvict = sorted.slice(0, overflow);

  await withStore('readwrite', (store) => {
    toEvict.forEach((entry) => store.delete(entry.key));
  });
};

/** Test/debug helper: list all keys currently in the cache. */
export const listOperationsCacheKeys = async (): Promise<string[]> => {
  const entries = await readAllEntries();
  return entries.map((e) => e.key);
};

/** Test helper: wipe the entire cache. */
export const clearOperationsCache = async (): Promise<void> => {
  await withStore('readwrite', (store) => {
    store.clear();
  });
};

/** Test/internal helper: reset the cached `dbPromise` so the next call reopens. */
export const __resetOperationsCacheDbForTests = (): void => {
  dbPromise = null;
};
